/* eslint-disable @typescript-eslint/unbound-method */
import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PanelContainer } from './PanelContainer'
import { ContentContainer, ContentSectionTitle } from './ContentContainer'
import { TextInput } from './TextInput'
import {
    phoneValidation,
    validationSchema,
    stringValidation,
    emailValidation,
    permissionValidation,
} from '../util/validation'
import { Spacer } from './Spacer'
import { InputPhoneNumber } from './TextInputPhoneNumber'
import { UserPermission, isUserPermissionSet, DraftUser } from '../models/client/user'
import { useModal } from '../hooks/useModal'
import { printError } from '../util/print'
import { useMessage } from '../hooks/useMessage'
import { BaseError } from '../util/errors/BaseError'
import { PermissionsSettings } from './PermissionsSettings'

type Props = {
    member: DraftUser
    isAdd?: boolean
    canDelete?: boolean
    canEditPermissions?: boolean
    className?: string
    operationsLoading?: boolean
    onSave: (draft: DraftUser) => Promise<void>
    onClose: () => void
    onDelete: (memberId: string) => Promise<void>
}

export const PanelUpsertUser = (props: Props): JSX.Element => {
    const {
        member,
        className,
        operationsLoading,
        isAdd,
        canDelete,
        canEditPermissions,
        onClose,
        onSave,
        onDelete,
    } = props
    const messenger = useMessage()
    const [Modal, triggerModal] = useModal({
        onCancel: () => {
            // TODO: fix this from conditional render on the function to just a param like 'disableCancel'
        },
        onConfirm: () => {
            if (!member.id) {
                messenger.error(new BaseError({ message: 'Something Went Wrong' }))
                return
            }
            onDelete(member.id)
        },
    })

    const initialValues = useMemo(
        () => ({
            name: member?.name || '',
            email: member?.email || '',
            phone: member?.phone || '',
            permissions: member?.permissions || [],
            password: '',
            confirmPassword: '',
        }),
        [member?.email, member?.name, member?.permissions, member?.phone]
    )

    const { control, formState, reset, getValues, trigger, setError } = useForm({
        defaultValues: initialValues,
        resolver: zodResolver(
            validationSchema({
                name: stringValidation({ required: true }),
                email: emailValidation({ required: true }),
                phone: phoneValidation(),
                permissions: permissionValidation,
                password: stringValidation(),
                confirmPassword: stringValidation(),
            })
        ),
    })

    const handleSave = async (): Promise<void> => {
        const isValid = await trigger()
        const { email, name, phone, permissions, password, confirmPassword } = getValues()

        const hasPassword = password?.length

        if (hasPassword) {
            if (!password && !confirmPassword?.length) {
                setError('password', { message: 'required' })
                setError('confirmPassword', { message: 'required' })
                return
            }
            if (password !== confirmPassword) {
                setError('confirmPassword', {
                    message: 'Confirmation must match password',
                })
                return
            }
        }

        if (!isValid) {
            return
        }

        // if (!member || !email || !name || !isUserPermissionSet(permissions)) {
        //     throw new Error('System Error, Please Contact Support')
        // }

        try {
            let userObject: DraftUser = {
                id: member.id,
                email,
                name,
                phone,
            }

            if (hasPassword) {
                userObject = {
                    ...userObject,
                    password,
                } as DraftUser
            }

            if (canEditPermissions) {
                userObject = {
                    ...userObject,
                    permissions,
                } as DraftUser
            }

            await onSave(userObject)
        } catch (error) {
            printError(`PanelEditUser ERROR`, error)
        }
    }

    const handleDelete = async (): Promise<void> => {
        if (!member) return
        if (!member.id) return
        try {
            await triggerModal(true)
            await onDelete(member.id)
            onClose()
        } catch (e) {
            reset()
        }
    }

    const { errors } = formState
    const { password: passwordError, confirmPassword: confirmPasswordError } = errors

    const showPasswordInputs = true
    const showPermissionsInputs = canEditPermissions

    const PasswordInputs = useMemo(() => {
        if (showPasswordInputs)
            return (
                <>
                    <Controller
                        control={control}
                        name="password"
                        render={({ onChange, value }) => {
                            return (
                                <TextInput
                                    label="Password"
                                    type="password"
                                    value={value as string}
                                    onChange={(nextValue) => onChange(nextValue)}
                                    errorMessage={passwordError?.message}
                                />
                            )
                        }}
                    />
                    <Spacer vertical size={1} />
                    <Controller
                        control={control}
                        name="confirmPassword"
                        render={({ onChange, value }) => {
                            return (
                                <TextInput
                                    label="Confirm Password"
                                    value={value as string}
                                    type="password"
                                    onChange={(nextValue) => onChange(nextValue)}
                                    errorMessage={confirmPasswordError?.message}
                                />
                            )
                        }}
                    />
                    <Spacer vertical size={1} />
                </>
            )
        return <div />
    }, [showPasswordInputs, isAdd, control, passwordError, confirmPasswordError])

    const PermissionsInputs = useMemo(() => {
        if (showPermissionsInputs) {
            return (
                <>
                    <SectionTitle>Permissions</SectionTitle>
                    <Content>
                        <Controller
                            control={control}
                            name="permissions"
                            render={({ value, onChange }) => {
                                const permissionList = value as Array<UserPermission>

                                return (
                                    <PermissionsSettings
                                        selections={permissionList}
                                        onChange={onChange}
                                    />
                                )
                            }}
                        />
                    </Content>
                </>
            )
        }
        return <div />
    }, [showPermissionsInputs])

    return (
        <PanelContainer
            className={className}
            header={isAdd ? 'Add User' : 'Edit User'}
            subheader={
                isAdd ? 'Add a user to your organization' : 'Modify user information or permissions'
            }
            saveLabel={isAdd ? 'Add' : 'Save'}
            onClose={onClose}
            onSave={handleSave}
            onCancel={() => {
                reset({ phone: '123' })
            }}
            onDelete={isAdd ? undefined : handleDelete}
            disableCancel={!formState.isDirty || operationsLoading}
            disableSave={!formState.isDirty || operationsLoading}
            disableDelete={!canDelete || formState.isDirty || operationsLoading}
        >
            <Container>
                <Modal confirmLabel="Yes" cancelLabel="No">
                    <div>Are you sure you want to delete this user?</div>
                </Modal>
                <Content>
                    <Controller
                        control={control}
                        name="name"
                        render={({ onChange, value }) => {
                            return (
                                <TextInput
                                    label="Name"
                                    value={value as string}
                                    onChange={(nextValue) => onChange(nextValue)}
                                    errorMessage={errors.name?.message}
                                />
                            )
                        }}
                    />
                    <Spacer vertical size={2} />
                    <Controller
                        control={control}
                        name="email"
                        render={({ onChange, value }) => {
                            return (
                                <TextInput
                                    label="Email"
                                    value={value as string}
                                    onChange={(nextValue) => onChange(nextValue)}
                                    errorMessage={errors.email?.message}
                                />
                            )
                        }}
                    />

                    <Spacer vertical size={2} />

                    {PasswordInputs}
                </Content>

                <Spacer vertical size={4} />

                {PermissionsInputs}
            </Container>
        </PanelContainer>
    )
}

const Container = styled.div``

const Content = styled(ContentContainer)`
    display: flex;
    flex-flow: column nowrap;
`

const SectionTitle = styled(ContentSectionTitle)``
